import { Icon, IconButton, MenuItem } from "@material-ui/core";
import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";
import { Trans } from "react-i18next";
import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResource,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
    WorkspacePlanTier
} from "common/interfaces";
import WorkspaceController, {
    getWorkspaceResources,
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { openTeamUpgrade } from "js/core/utilities/externalLinks";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { UpgradeTooltip } from "js/Components/UpgradeTooltip";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowSnackBar,
    ShowUpgradeDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { FlexSpacer, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Divider, IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { getAddToFolderSnackbarProps } from "js/editor/PresentationLibrary/utils/getAddToFolderSnackbarProps";
import { moveSubfolderContentsToParentFolder } from "js/react/views/SharedResources/utils/moveSubfolderContentsToParentFolder";
import {
    AddPresentationsToFolderDialog,
    ManageTeamFolderMembersDialog,
} from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import { t } from "js/i18n";
import PresentationLibraryPaneController, { PresentationLibraryFilter, PresentationLibraryFilterType, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";

const logger = getLogger();

export const SideBarItem = styled.div`
    width: 100%;
    color: #444;
    font-size: 14px;
    letter-spacing: 0px;
    font-weight: 600;
    display: flex;
    flex-shrink: 0;
    margin-bottom: 0px;
    align-items: flex-start;
    overflow: hidden;
    padding: 12px 10px;
    transition: background-color 300ms;
    background: none;
    cursor: pointer;

    & > .material-icons {
        font-size: 18px;
        margin-right: 10px;
        color: #666;
    }

    //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
    &:hover,
    &.hover {
        background: ${themeColors.hoverBlue};
    }

    &.selected {
        cursor: default;
        background: ${themeColors.lightBlue};

        & > .material-icons {
            color: ${themeColors.ui_blue};
        }

        //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover,
        &.hover {
            background: ${themeColors.lightBlue};
        }
    }

    .folder-name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dark-mode & {
        color: #ccc;
        background: none;

        & > .material-icons {
            color: #aaa;
        }

        //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover {
            background: #555;
        }

        &.selected {
            background: #333;

            & > .material-icons {
                color: ${themeColors.ui_blue};
            }

            //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
            &:hover {
                background: #333;
            }
        }
    }
`;

const FolderContainer = styled.div`
  width: 100%;
  position: relative;
`;

const FolderLabel = styled.div`
    padding: 0 8px;
`;

const SideBarLabel = styled.div`
    width: 100%;
    font-size: 12px;
    display: flex;
    align-items: center;
    text-transform: uppercase;

    color: ${themeColors.mediumGray};
    padding: 6px 0px;

    .dark-mode & {
        color: #bbb;
    }

    & > *:nth-child(3) {
        margin-left: auto;
        margin-right: 10px;
    }
`;

const LibraryFolder = styled(SideBarItem)`
    padding: 6px 0px;
    align-items: center;

    .MuiIconButton-root {
        padding: 0px 0px;
        opacity: 0;
    }

    .expand-arrow {
        font-size: 24px;
        margin-right: 0;
    }

    &:hover {
        .MuiIconButton-root {
            opacity: 1;
        }
    }
`;

const SubLibraryFolder = styled(LibraryFolder)`
    margin-left: 40px;
    width: calc(100% - 40px);
`;

const StyledFlexBox = styled(FlexBox)`
    height: ${props => (props as any).$canEditLibraryItems ? `calc(100% - 90px)`
        : `calc(100% - 40px)`};
`;

const StyledTeamFolders = styled.div`
    overflow: auto;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const TrashSideBarContainer = styled.div`
    width: 100%;
    background-color: #ffffff;
`;

const StyledAddIcon = styled(Icon)`
    &&& {
        font-size: 20px;
    }
`;

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

// NOTE: We have to use `className="selected"` instead of `props.selected`
//   because a bug with `styled and` the application of `.dark-mode`
function selectedClass(selected: boolean) {
    return selected ? "selected" : "";
}

async function getPresentationResourcesByIds(resourceIds: string[]): Promise<IWorkspaceResource[]> {
    if (resourceIds.length === 0) {
        return [];
    }

    return getWorkspaceResources({
        workspaceId: WorkspaceController.workspaceId,
        resourceTypes: [WorkspaceResourceType.PRESENTATION],
        resourceFilters: [{ field: "resourceId", operator: "in", value: resourceIds }]
    });
}

async function getPresentationResourcesInFolder(folderId: string): Promise<IWorkspaceResource[]> {
    return getWorkspaceResources({
        workspaceId: WorkspaceController.workspaceId,
        resourceTypes: [WorkspaceResourceType.PRESENTATION],
        resourceFilters: [{ field: "folderIds", operator: "eq", value: folderId }]
    });
}

function isPresentationFolderNameTaken(
    name: string,
    folders: IWorkspaceResourceFolder[],
    {
        parentFolderId,
        folderTypes,
        excludeFolderId,
    }: {
        parentFolderId?: string | null;
        folderTypes?: WorkspaceResourceFolderType[];
        excludeFolderId?: string;
    }
) {
    return folders
        .filter(f => {
            if (f.resourcesType !== WorkspaceResourceType.PRESENTATION) {
                return false;
            }
            if (excludeFolderId && f.id === excludeFolderId) {
                return false;
            }
            if (folderTypes && !folderTypes.includes(f.folderType)) {
                return false;
            }
            if (parentFolderId) {
                return f.parentFolderId === parentFolderId;
            }
            return !f.parentFolderId;
        })
        .some(f => f.name === name);
}

interface PresentationLibrarySideBarProps {
    readOnly: boolean;
    filter: PresentationLibraryFilter;
    canEditLibraryItems: boolean;
    draggedItems: PresentationLibraryPresentation[];
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    onHideSidebar?: () => void;
}

export const PresentationLibrarySideBar = WorkspaceController.withInitializedState(
    function PresentationLibrarySideBar(props: PresentationLibrarySideBarProps & WorkspaceControllerState) {
        const { readOnly, filter, onHideSidebar, canEditLibraryItems, workspace: { id: workspaceId }, onSetFilter, resourceFolders, draggedItems } = props;

        const [isPersonalFoldersExpanded, setIsPersonalFoldersExpanded] = useState(true);
        const [isTeamFoldersExpanded, setIsTeamFoldersExpanded] = useState(WorkspaceController.isMultiUserWorkspace);
        const isGuest = WorkspaceController.role === WorkspaceUserRole.GUEST;
        const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
        const canUpgradeToCreateFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);
        const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
        const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

        const userFolders = resourceFolders.filter(f => f.folderType === WorkspaceResourceFolderType.PERSONAL)
            .sort((a, b) => a.name.localeCompare(b.name));
        const teamFolders =
            [
                ...resourceFolders.filter(folder => !folder.parentFolderId && folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT),
                ...resourceFolders.filter(folder =>
                    !folder.parentFolderId &&
                    folder.folderType === WorkspaceResourceFolderType.SHARED &&
                    folder.resourcesType === WorkspaceResourceType.PRESENTATION)
                    .sort((a, b) => a.name.localeCompare(b.name))
            ];

        const handleSelectFilter = (filterType: PresentationLibraryFilterType) => {
            onSetFilter({ type: filterType });
            onHideSidebar?.();
        };

        const handleTrashMouseUp = () => {
            if (draggedItems.length > 0) {
                PresentationLibraryPaneController.trashPresentations(draggedItems.map(p => p.id));
                return;
            }

            onSetFilter({ type: PresentationLibraryFilterType.TRASH });
        };

        const handleCreateFolder = async (folderType: WorkspaceResourceFolderType) => {
            setIsPersonalFoldersExpanded(true);

            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create) {
                if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
                    ShowUpgradeDialog({
                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                        analytics: { cta: "Folder" },
                        workspaceId
                    });
                    return;
                }

                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You do not have permission to create folders!")
                });
                return;
            }

            // Legacy limit
            if (folderType === WorkspaceResourceFolderType.PERSONAL && userFolders.length > 49) {
                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You have reached the folder limit!")
                });
                return;
            }

            const folderName: string = await new Promise(resolve => ShowDialog(InputConfirmationDialog, {
                title: folderType === WorkspaceResourceFolderType.PERSONAL ? t("Create Folder") : t("Create Team Folder"),
                input: {
                    type: "text",
                    placeholder: t("Type folder name..."),
                    id: "create_folder_input",
                    inputProps: {
                        maxLength: 50
                    }
                },
                acceptCallback: (input: string) => resolve(input.trim())
            }));

            if (isPresentationFolderNameTaken(folderName, resourceFolders, { parentFolderId: null, folderTypes: [folderType] })) {
                ShowWarningDialog({
                    title: t("Unable to create folder"),
                    message: t("Please enter a unique folder name.")
                });
                return;
            }

            try {
                const folder = await withProgressDialog({
                    title: folderType === WorkspaceResourceFolderType.PERSONAL ? t("Creating folder...") : t("Creating team folder..."),
                    action: () => WorkspaceController.createWorkspaceResourceFolder({ name: folderName, resourcesType: WorkspaceResourceType.PRESENTATION, folderType })
                });

                if (folderType === WorkspaceResourceFolderType.SHARED) {
                    ShowDialog(ManageTeamFolderMembersDialog, {
                        folderId: folder.id
                    });
                }
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar] handleCreateFolder() Unable to create folder");

                ShowErrorDialog({
                    title: t("Unable to create folder"),
                    message: err.message
                });
            }
        };

        return (
            <>
                {/* @ts-ignore */}
                <StyledFlexBox column
                               className="library-sidebar"
                               verticalAlign="start"
                               horizontalAlign="start"
                               $canEditLibraryItems={canEditLibraryItems}
                >
                    <SideBarItem
                        className={selectedClass(filter.type == PresentationLibraryFilterType.ALL_PRESENTATIONS)}
                        onClick={() => handleSelectFilter(PresentationLibraryFilterType.ALL_PRESENTATIONS)}
                        id="all-presentations"
                        data-test-id="sidebar-nav-all-presentations"
                    >
                        <Icon>select_all</Icon>
                        {t("All Presentations")}
                    </SideBarItem>
                    <SideBarItem
                        className={selectedClass(filter.type == PresentationLibraryFilterType.RECENT)}
                        onClick={() => handleSelectFilter(PresentationLibraryFilterType.RECENT)}
                        id="rect"
                        data-test-id="sidebar-nav-recent"
                    >
                        <Icon>history</Icon>
                        {t("Recent Presentations")}
                    </SideBarItem>
                    <SideBarItem
                        className={selectedClass(filter.type == PresentationLibraryFilterType.OWNED_BY_ME)}
                        onClick={() => handleSelectFilter(PresentationLibraryFilterType.OWNED_BY_ME)}
                        id="owned-by-me"
                        data-test-id="sidebar-nav-owned-by-me"
                    >
                        <Icon>person_pin</Icon>
                        {t("Created By Me")}
                    </SideBarItem>
                    <SideBarItem
                        className={selectedClass(filter.type == PresentationLibraryFilterType.SHARED_WITH_ME)}
                        onClick={() => handleSelectFilter(PresentationLibraryFilterType.SHARED_WITH_ME)}
                        id="shared-with-me"
                        data-test-id="sidebar-nav-shared-with-me"
                    >
                        <Icon>share</Icon>
                        {t("Shared With Me")}
                    </SideBarItem>
                    {/* @ts-ignore */}
                    <Divider margin={10} />
                    <StyledTeamFolders>
                        {/* User Folders */}
                        {(canCreateFolders || userFolders.length > 0 || canUpgradeToCreateFolders) && <>
                            <SideBarLabel data-test-id="my-folders-section">
                                <Icon
                                    onMouseUp={() => setIsPersonalFoldersExpanded(!isPersonalFoldersExpanded)}
                                    className="expand-arrow">{isPersonalFoldersExpanded ? "arrow_drop_down" : "arrow_right"}
                                </Icon>
                                <FolderLabel>{t("My Folders")}</FolderLabel>
                                {!readOnly && !app.isConstrained && canCreateFolders &&
                                    <IconButton
                                        size="small"
                                        id="create-my-folder-button"
                                        data-test-id="create-my-folder-button"
                                        onClick={() => handleCreateFolder(WorkspaceResourceFolderType.PERSONAL)}
                                    >
                                        <StyledAddIcon>add</StyledAddIcon>
                                    </IconButton>
                                }
                                {canUpgradeToCreateFolders &&
                                    <UpgradeTooltip
                                        tooltipTitle="Unlock Presentation Folders"
                                        tooltipText="Upgrade your plan to organize your presentations into folders."
                                        onUpgrade={() => ShowDialog(ChangePlanDialog, {
                                            featuredPlan: WorkspacePlanTier.PRO,
                                            analytics: { cta: "unlock_my_folders" }
                                        })}
                                    >
                                        <UpgradeIcon />
                                    </UpgradeTooltip>
                                }
                            </SideBarLabel>
                            {isPersonalFoldersExpanded && <FolderContainer>
                                {userFolders.map((userFolder, idx) => (
                                    <UserFolder
                                        key={idx}
                                        folder={userFolder}
                                        readOnly={readOnly}
                                        onSetFilter={onSetFilter}
                                        draggedItems={draggedItems}
                                        filter={filter}
                                        onHideSidebar={onHideSidebar}
                                    />
                                ))}
                            </FolderContainer>}
                        </>}
                        {/* Team Folders */}
                        {((canUseSharedResources && (canCreateFolders || teamFolders.length > 0)) || (canUpgradeToUseSharedResources || canUpgradeToManageSharedResources)) && <>
                            <SideBarLabel data-test-id="team-folders-section">
                                {<Icon
                                    onMouseUp={() => setIsTeamFoldersExpanded(!isTeamFoldersExpanded)}
                                    className="expand-arrow">{isTeamFoldersExpanded ? "arrow_drop_down" : "arrow_right"}
                                </Icon>}
                                <FolderLabel>{t("Team Folders")}</FolderLabel>
                                {
                                    !readOnly && !app.isConstrained && canCreateFolders && canUseSharedResources &&
                                    <IconButton
                                        size="small"
                                        id="create-team-folder-button"
                                        data-test-id="create-team-folder-button"
                                        onClick={() => handleCreateFolder(WorkspaceResourceFolderType.SHARED)}
                                    >
                                        <StyledAddIcon>add</StyledAddIcon>
                                    </IconButton>
                                }
                                {(canUpgradeToUseSharedResources || canUpgradeToManageSharedResources) &&
                                    <UpgradeTooltip
                                        tooltipTitle="Unlock Team Folders"
                                        tooltipText="Upgrade to a Team plan to keep your team’s presentations organized and easy to find."
                                        onUpgrade={() => ShowDialog(ChangePlanDialog, {
                                            featuredPlan: WorkspacePlanTier.TEAM,
                                            analytics: { cta: "unlock_team_folders" }
                                        })}
                                    >
                                        <UpgradeIcon />
                                    </UpgradeTooltip>
                                }
                            </SideBarLabel>
                            {isTeamFoldersExpanded && <FolderContainer>
                                {teamFolders.map((folder, idx) => (
                                    <TeamFolder
                                        key={idx}
                                        folder={folder}
                                        readOnly={readOnly}
                                        onSetFilter={onSetFilter}
                                        draggedItems={draggedItems}
                                        filter={filter}
                                        onHideSidebar={onHideSidebar}
                                    />
                                ))}
                            </FolderContainer>}
                            <Gap20 />
                        </>}

                    </StyledTeamFolders>
                </StyledFlexBox>

                {!readOnly && !app.isConstrained &&
                    <TrashSideBarContainer>
                        <Divider margin={0} />
                        <SideBarItem
                            className={selectedClass(filter.type == PresentationLibraryFilterType.TRASH)}
                            onMouseUp={handleTrashMouseUp}
                            id="trash"
                            data-test-id="sidebar-nav-trash"
                        >
                            <Icon>delete</Icon>
                            {t("Trash")}
                        </SideBarItem>
                    </TrashSideBarContainer>
                }

                {isGuest &&
                    <>
                        <Divider margin={10} />
                        <UpgradeSection
                            title={t("You're a guest")}
                            body={t("As a guest you can collaborate, but can't create new presentations. To unlock all of Beautiful.ai's features, create a workspace.")}
                            buttonText={t("Create a workspace")}
                            buttonIcon={<UpgradeIcon />}
                            buttonOnClick={openTeamUpgrade}
                        />
                    </>
                }
            </>
        );
    }
);

interface FolderProps {
    folder: IWorkspaceResourceFolder;
    readOnly: boolean;
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    draggedItems: PresentationLibraryPresentation[];
    filter: PresentationLibraryFilter;
    onHideSidebar?: () => void;
}

const UserFolder = WorkspaceController.withInitializedState(function UserFolder(props: FolderProps & WorkspaceControllerState) {
    const { folder, readOnly, onSetFilter, onHideSidebar, resourceFolders, draggedItems, filter } = props;
    const canRenameUserFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameUserFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);
    const canDeleteUserFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToDeleteUserFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: null,
                folderTypes: [WorkspaceResourceFolderType.PERSONAL],
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            const resourcesInFolder = await getPresentationResourcesInFolder(folder.id);
            if (resourcesInFolder.length > 0) {
                throw new Error(t("This folder contains presentations and can't be deleted."));
            }

            await withProgressDialog({
                title: t("Deleting folder..."),
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
            });

            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleDeleteFolder() Unable to delete folder");

            ShowErrorDialog({
                title: t("Unable to delete folder"),
                message: err.message
            });
        }
    };

    const handleMouseUp = async (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (draggedItems.length === 0) {
            e.stopPropagation();
            e.preventDefault();
            onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.id });
            onHideSidebar?.();
            return;
        }

        if (!canRenameUserFolders) {
            if (canUpgradeToRenameUserFolders) {
                handleCanUpgradeAction("add_to_folder")();
            } else {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permission to add presentations to this folder.")
                });
            }
            return;
        }

        try {
            // Recover any presentations that are in the trash before adding them to a folder.
            if (draggedItems.some(item => item.trashedAt)) {
                await PresentationLibraryPaneController.recoverPresentations(draggedItems.filter(item => item.trashedAt).map(item => item.id));
            }

            await withProgressDialog({
                title: t("Moving presentations..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId: folder.id,
                    update: {
                        add: draggedItems.map(item => ({
                            resourceId: item.id,
                            resourceType: WorkspaceResourceType.PRESENTATION,
                            permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                        }))
                    }
                })
            });

            let snackbar;
            const handleClick = () => {
                onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.id });
                snackbar.close();
            };
            const { i18nKey, values } = getAddToFolderSnackbarProps(draggedItems.length, folder.name);
            snackbar = ShowSnackBar({
                message: (
                    <p>
                        <Trans
                            // @ts-ignore
                            t={t}
                            i18nKey={i18nKey}
                            values={values}
                            components={{ folderLink: <span className="action_item" onClick={handleClick} /> }}
                        />
                    </p>
                ),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleCanUpgradeAction = cta => () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta } });
        }
    };

    return (
        <LibraryFolder
            data-attr={folder.name}
            className={selectedClass(filter.type == PresentationLibraryFilterType.FOLDER && filter.folderId == folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon className="expand-arrow" />
            <Icon>folder</Icon>
            <div className="folder-name"> {folder.name}</div>
            <FlexSpacer />
            {!readOnly && !app.isConstrained &&
                <IconMenu>
                    {canRenameUserFolders &&
                        <MenuItem onClick={handleRenameFolder}>
                            <Icon>label</Icon>
                            {t("Rename Folder")}
                        </MenuItem>}
                    {canUpgradeToRenameUserFolders && (
                        <RestrictedMenuItem divider onClick={handleCanUpgradeAction("rename_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Rename Folder")}
                        </RestrictedMenuItem>
                    )}

                    <hr />
                    {canDeleteUserFolders &&
                        <MenuItem onClick={handleDeleteFolder}>
                            <Icon>delete</Icon>
                            {t("Delete Folder")}
                        </MenuItem>}

                    {canUpgradeToDeleteUserFolders &&
                        <RestrictedMenuItem onClick={handleCanUpgradeAction("delete_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Delete Folder")}
                        </RestrictedMenuItem>}
                </IconMenu>
            }
        </LibraryFolder>
    );
});

const TeamFolder = WorkspaceController.withInitializedState(function TeamFolder(props: FolderProps & WorkspaceControllerState) {
    const { folder, workspace: { id: workspaceId }, onHideSidebar, filter, readOnly, onSetFilter, draggedItems, resourceFolders } = props;
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
    const [isExpanded, setIsExpanded] = useState(false);

    const isDefault = folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
    const subFolders = resourceFolders
        .filter(f => f.resourcesType === WorkspaceResourceType.PRESENTATION && f.folderType === WorkspaceResourceFolderType.SHARED && f.parentFolderId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleCanUpgradeAction = () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.TEAM });
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const fetchedPermissions = await getUserPermissionsToResourceFolder({
                    workspaceId: workspaceId,
                    folderId: folder.id,
                });
                setPermissions(fetchedPermissions);
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar][TeamFolder] useEffect() Unable to fetch permissions");
            }
        })();
    }, [folder.id]);

    const handleMouseUp = async () => {
        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationLibraryFilterType.TEAM_FOLDER, folderId: folder.id });
            if (subFolders.length === 0) {
                onHideSidebar?.();
            }
            return;
        }

        if (!canUseSharedResources) {
            if (canUpgradeToUseSharedResources) {
                handleCanUpgradeAction();
            } else {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
            }
            return;
        }

        try {
            const { write } = await getUserPermissionsToResourceFolder({
                workspaceId,
                folderId: folder.id,
            });
            if (!write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
                return;
            }

            const resources = await getPresentationResourcesByIds(draggedItems.map(i => i.id));
            const subFolders = resourceFolders.filter(f => f.resourcesType === WorkspaceResourceType.PRESENTATION && f.folderType === WorkspaceResourceFolderType.SHARED && f.parentFolderId === folder.id);
            const presentationsNotInFolder = draggedItems.filter(item => {
                const resource = resources.find(r => r.resourceId === item.id);
                return !resource || (!resource.folderIds?.includes(folder.id) && !resource.folderIds?.some(id => subFolders.some(f => f.id === id)));
            });
            if (presentationsNotInFolder.length > 0) {
                ShowDialog(AddPresentationsToFolderDialog, {
                    folderId: folder.id,
                    presentationIds: draggedItems.map(i => i.id),
                });
            } else {
                await withProgressDialog({
                    title: t("Moving presentations..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId: folder.id,
                        update: {
                            add: draggedItems.map(i => ({
                                resourceId: i.id,
                                resourceType: WorkspaceResourceType.PRESENTATION,
                                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                            }))
                        }
                    })
                });
            }
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleEditSharing = () => {
        if (isDefault) {
            ShowWarningDialog({
                title: t("Sharing can't be modified"),
                message: t("The All Team Members folder is shared with your entire team and can't be modified or deleted.")
            });
            return;
        }

        ShowDialog(ManageTeamFolderMembersDialog, {
            folderId: folder.id
        });
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: null,
                folderTypes: [WorkspaceResourceFolderType.SHARED, WorkspaceResourceFolderType.SHARED_DEFAULT],
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message
            });
        }
    };

    const handleCreateSubFolder = async () => {
        let name = await ShowInputDialog({
            title: t("New folder"),
            maxChars: 100,
            value: t("New Folder")
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (!name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, { parentFolderId: folder.id })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            await withProgressDialog({
                title: t("Creating subfolder..."),
                action: () => WorkspaceController.createWorkspaceResourceFolder({
                    parentFolderId: folder.id,
                    name,
                    resourcesType: WorkspaceResourceType.PRESENTATION,
                    folderType: WorkspaceResourceFolderType.SHARED
                })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleCreateSubFolder() Unable to create subfolder");

            ShowErrorDialog({
                title: t("Unable to create subfolder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Delete this shared team folder?"),
            message: <Fragment>
                <p>{t("Presentations in the folder will NOT be deleted, but members may lose access.")}</p>
                <br />
                <p>{t("You cannot undo this action.")}</p>
            </Fragment>
        })) {
            try {
                await withProgressDialog({
                    title: t("Deleting folder..."),
                    action: async () => {
                        for (const subFolder of subFolders) {
                            await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: subFolder.id });
                        }

                        await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id });
                    },
                });

                onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });
            } catch (err) {
                if (err) {
                    logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleDeleteFolder() failed", { folderId: folder.id });

                    ShowErrorDialog({
                        error: t("Failed to delete team folder"),
                        message: t("Something went wrong. Try again or contact support@beautiful.ai if the issue persists.")
                    });
                }
            }
        }
    };

    const handleLeaveFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Leave this team folder?"),
            message: t("You may lose access to presentations in this folder. You can rejoin only if a team owner or librarian invites you.")
        })) {
            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });

            try {
                await withProgressDialog({
                    title: t("Leaving team folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: { remove: [{ subjectId: app.user.id, subjectType: WorkspacePermissionSubjectType.USER }] }
                    })
                });
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleLeaveFolder() Unable to leave team folder");

                ShowErrorDialog({
                    title: t("Unable to leave team folder"),
                    message: err.message
                });
            }
        }
    };

    // TODO: Extract the possible action permission for better reuse
    const canUpgradeToRenameTeamFolder = canUpgradeToManageSharedResources;
    const canRenameTeamFolder = !canUpgradeToRenameTeamFolder && permissions.write.object && !isDefault;
    const canUpgradeToCreateSubfolder = canUpgradeToManageSharedResources;
    const canCreateSubfolder = !canUpgradeToCreateSubfolder && permissions.write.object;

    return (
        <Fragment>
            <LibraryFolder
                data-attr={folder.name}
                data-attr-id={folder.id}
                className={selectedClass(filter.type == PresentationLibraryFilterType.TEAM_FOLDER && filter.folderId == folder.id && !filter.subFolderId)}
                onMouseUp={handleMouseUp}
                onTouchEnd={handleMouseUp}
            >
                {subFolders.length > 0
                    ? <Icon onMouseUp={() => setIsExpanded(!isExpanded)} className="expand-arrow">{isExpanded ? "arrow_drop_down" : "arrow_right"}</Icon>
                    : <Icon className="expand-arrow" />
                }
                <Icon>folder_shared</Icon>
                <div className="folder-name">
                    {folder.name}
                </div>
                <FlexSpacer />
                {!readOnly && !app.isConstrained &&
                    <IconMenu>
                        {!isDefault && !canUpgradeToManageSharedResources && (
                            <MenuItem divider onClick={handleEditSharing}>
                                <Icon>people_add</Icon>
                                {permissions.write.permissions ? t("Share Team Folder...") : t("View Sharing...")}
                            </MenuItem>
                        )}
                        {canUpgradeToManageSharedResources && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Share Team Folder...")}
                            </RestrictedMenuItem>
                        )}
                        {canRenameTeamFolder && (
                            <MenuItem divider onClick={handleRenameFolder}>
                                <Icon>label</Icon>
                                {t("Rename Team Folder...")}
                            </MenuItem>
                        )}
                        {canUpgradeToRenameTeamFolder && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Rename Team Folder...")}
                            </RestrictedMenuItem>
                        )}
                        {canCreateSubfolder && (
                            <MenuItem divider onClick={handleCreateSubFolder}>
                                <Icon>folder_open</Icon>
                                {t("Create SubFolder...")}
                            </MenuItem>
                        )}
                        {canUpgradeToCreateSubfolder && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Create SubFolder...")}
                            </RestrictedMenuItem>
                        )}
                        {permissions.owner && !isDefault && !canUpgradeToManageSharedResources && (
                            <MenuItem onClick={handleDeleteFolder}>
                                <Icon>delete</Icon>
                                {t("Delete Team Folder")}
                            </MenuItem>
                        )}
                        {permissions.owner && !isDefault && canUpgradeToManageSharedResources && (
                            <RestrictedMenuItem onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Delete Team Folder")}
                            </RestrictedMenuItem>
                        )}
                        {!permissions.owner && !isDefault && (
                            <MenuItem onClick={handleLeaveFolder}>
                                <Icon>logout</Icon>
                                {t("Leave Team Folder")}
                            </MenuItem>
                        )}
                    </IconMenu>
                }
            </LibraryFolder>
            {isExpanded &&
                subFolders.map(subFolder => (
                    <TeamSubFolder
                        key={subFolder.id}
                        folder={subFolder}
                        readOnly={readOnly}
                        filter={filter}
                        onSetFilter={onSetFilter}
                        draggedItems={draggedItems}
                        permissions={permissions}
                        onHideSidebar={onHideSidebar}
                    />
                ))}
        </Fragment >
    );
});

const TeamSubFolder = WorkspaceController.withInitializedState(function TeamSubFolder(props: FolderProps & WorkspaceControllerState & { permissions: IWorkspacePermissionAccess }) {
    const { folder, readOnly, filter, onHideSidebar, onSetFilter, permissions, resourceFolders, draggedItems, workspace: { id: workspaceId } } = props;
    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleCanUpgradeAction = () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.TEAM });
        }
    };

    const handleMouseUp = async () => {
        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationLibraryFilterType.TEAM_FOLDER, folderId: folder.parentFolderId, subFolderId: folder.id });
            onHideSidebar?.();
            return;
        }

        if (!canUseSharedResources) {
            if (canUpgradeToUseSharedResources) {
                handleCanUpgradeAction();
            } else {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
            }
            return;
        }

        try {
            const { write } = await getUserPermissionsToResourceFolder({
                workspaceId,
                folderId: folder.id
            });
            if (!write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
                return;
            }

            const parentFolderAndItsSubfolderIds = [folder.parentFolderId, ...resourceFolders.filter(f => f.parentFolderId === folder.parentFolderId).map(f => f.id)];
            const draggedResources = await getPresentationResourcesByIds(draggedItems.map(i => i.id));
            const draggedResourcesNotInParentFolder = draggedItems.filter(item => {
                const resource = draggedResources.find(r => r.resourceId === item.id);
                return !resource || !resource.folderIds?.some(id => parentFolderAndItsSubfolderIds.includes(id));
            });
            if (draggedResourcesNotInParentFolder.length > 0) {
                await ShowDialogAsync(AddPresentationsToFolderDialog, {
                    folderId: folder.id,
                    presentationIds: draggedItems.map(r => r.id)
                });
            } else {
                await withProgressDialog({
                    title: t("Moving presentations..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId: folder.id,
                        update: {
                            add: draggedItems.map(r => ({
                                resourceId: r.id,
                                resourceType: WorkspaceResourceType.PRESENTATION,
                                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                            }))
                        }
                    })
                });
            }
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Subfolder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: folder.parentFolderId,
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            await withProgressDialog({
                title: t("Renaming subfolder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamSubFolder] handleRenameFolder() Unable to rename subfolder");

            ShowErrorDialog({
                title: t("Unable to rename subfolder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            const resourcesInFolder = await getPresentationResourcesInFolder(folder.id);
            if (resourcesInFolder.length > 0) {
                if (!await ShowConfirmationDialog({
                    title: t("Delete this subfolder?"),
                    message: <p>{t("Presentations will move back to the root team folder and will not be deleted.")}</p>
                })) {
                    return;
                }
            }

            await withProgressDialog({
                title: t("Deleting subfolder..."),
                action: async () => {
                    await moveSubfolderContentsToParentFolder(
                        folder,
                        [WorkspaceResourceType.PRESENTATION],
                        {
                            read: { object: true, permissions: true },
                            write: { object: true, permissions: true }
                        }
                    );
                    await WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id });
                },
            });

            onSetFilter({ type: PresentationLibraryFilterType.TEAM_FOLDER, folderId: folder.parentFolderId });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamSubFolder] handleDeleteFolder() Unable to delete subfolder");

            ShowErrorDialog({
                title: t("Unable to delete subfolder"),
                message: err.message
            });
        }
    };

    return (
        <SubLibraryFolder
            data-attr={folder.name}
            className={selectedClass(filter.type == PresentationLibraryFilterType.TEAM_FOLDER && filter.subFolderId == folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon>folder_open</Icon>
            <div className="folder-name">{folder.name}</div>

            <FlexSpacer />

            {!readOnly && !app.isConstrained && permissions.write.object && !canUpgradeToManageSharedResources &&
                <IconMenu>
                    <MenuItem onClick={handleRenameFolder}>
                        <Icon>label</Icon>
                        {t("Rename Folder")}
                    </MenuItem>
                    <hr />
                    <MenuItem onClick={handleDeleteFolder}>
                        <Icon>delete</Icon>
                        {t("Delete Folder")}
                    </MenuItem>
                </IconMenu>
            }
            {canUpgradeToManageSharedResources &&
                <IconMenu>
                    <RestrictedMenuItem onClick={handleCanUpgradeAction}>
                        <UpgradeIcon style={{ marginRight: 8 }} />
                        {t("Rename Folder")}
                    </RestrictedMenuItem>
                    <hr />
                    <RestrictedMenuItem onClick={handleCanUpgradeAction}>
                        <UpgradeIcon style={{ marginRight: 8 }} />
                        {t("Delete Folder")}
                    </RestrictedMenuItem>
                </IconMenu>
            }
        </SubLibraryFolder>
    );
});
